/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';
import {
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';

import { customLabelAndNoAriaLabelWarningMessage } from '../_common/messages';
import { type CommonProps, type TooltipObjectProps } from '../_common/types';
import { needleWarningMessage } from '../_common/utils';
import {
  ExclamationCircleIconSolid,
  InformationCircleIconOutline,
} from '../icons';
import { Skeleton } from '../skeleton';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';

type TextInputProps = {
  /** Assistive text displayed below the input */
  helpText?: string | ReactNode;

  /** Error message displayed below the input. When provided, it overrides `helpText` and shows an error icon */
  errorText?: string | ReactNode;

  /** Leading element rendered inside the input */
  leadingElement?: ReactElement;

  /** Trailing element rendered inside the input */
  trailingElement?: ReactElement;

  /** Size of the input */
  size?: 'small' | 'medium' | 'large';

  /** Whether the input is read-only */
  isReadOnly?: boolean;

  /**
   *  Whether to display the Required/Optional label next to the field label.
   *  Shows Required if isRequired is true, otherwise shows Optional.
   * @default false
   */
  showRequiredOrOptionalLabel?: boolean;

  /** Whether the input is disabled */
  isDisabled?: boolean;

  /** Whether the input is required */
  isRequired?: boolean;

  /** The current value of the input */
  value?: React.ComponentPropsWithoutRef<'input'>['value'];

  /** Placeholder text displayed when the input is empty */
  placeholder?: React.ComponentPropsWithoutRef<'input'>['placeholder'];

  /** Callback function triggered when the input value changes */
  onChange?: React.ComponentPropsWithoutRef<'input'>['onChange'];

  /** Whether the input should take the full available width */
  isFluid?: boolean;

  /** Text displayed in the information tooltip shown next to the label */
  moreInformationText?: React.ReactNode;

  /** The label displayed above the input */
  label?: string | React.ReactNode;

  /** Props for the Tooltip component used by the information icon */
  tooltipProps?: TooltipObjectProps;

  /** Whether to render skeletons instead of content */
  isSkeletonLoading?: boolean;

  /** Additional props forwarded to the underlying Skeleton components */
  skeletonProps?: React.ComponentProps<typeof Skeleton>;
};

/**
 *
 *
 * TextInput Component
 *
 *
 */

export const TextInput = ({
  label,
  isFluid,
  errorText,
  helpText,
  leadingElement,
  trailingElement,
  showRequiredOrOptionalLabel = false,
  moreInformationText,
  size = 'medium',
  placeholder,
  value,
  tooltipProps,
  htmlAttributes,
  isDisabled,
  isReadOnly,
  isRequired,
  onChange,
  className,
  style,
  isSkeletonLoading = false,
  skeletonProps,
  ref,
  ...restProps
}: CommonProps<'input', TextInputProps>) => {
  const containerWrappingClasses = classNames(`ndl-text-input`, className, {
    'ndl-disabled': isDisabled,
    'ndl-has-error': errorText,
    'ndl-has-icon': leadingElement || trailingElement || errorText,
    'ndl-has-leading-icon': leadingElement,
    'ndl-has-trailing-icon': trailingElement || errorText,
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-read-only': isReadOnly,
    'ndl-small': size === 'small',
  });

  const hasEmptyLabelValue =
    label === undefined || label === null || label === '';

  const labelWrappingClasses = classNames('ndl-form-item-label', {
    'ndl-fluid': isFluid,
    'ndl-form-item-no-label': hasEmptyLabelValue,
  });

  const combinedHtmlInputAttributes: InputHTMLAttributes<HTMLInputElement> = {
    ...htmlAttributes,
    className: classNames('ndl-input', htmlAttributes?.className),
  };
  const ariaLabel = combinedHtmlInputAttributes['aria-label'];
  const isCustomLabel = Boolean(label) && typeof label !== 'string';
  const hasCustomLabelAndNoAriaLabel =
    isCustomLabel && (ariaLabel === undefined || ariaLabel === '');

  useMemo(() => {
    if (!label && !ariaLabel) {
      needleWarningMessage(
        'A TextInput without a label does not have an aria label, be sure to include an aria label for screen readers. Link: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI',
      );
    }

    if (hasCustomLabelAndNoAriaLabel) {
      needleWarningMessage(customLabelAndNoAriaLabelWarningMessage);
    }
  }, [label, ariaLabel, hasCustomLabelAndNoAriaLabel]);

  const informationIconClasses = classNames({
    'ndl-information-icon-large': size === 'large',
    'ndl-information-icon-small': size === 'small' || size === 'medium',
  });

  return (
    <div className={containerWrappingClasses} style={style}>
      {/* We enable implicit label wrapping */}
      {/* Source: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI */}
      <label className={labelWrappingClasses}>
        {!hasEmptyLabelValue && (
          <Skeleton
            onBackground="weak"
            shape="rectangular"
            {...skeletonProps}
            isLoading={isSkeletonLoading}
          >
            <div className="ndl-label-text-wrapper">
              <Typography
                variant={size === 'large' ? 'body-large' : 'body-medium'}
                className="ndl-label-text"
              >
                {label}
              </Typography>
              {Boolean(moreInformationText) && (
                <Tooltip {...tooltipProps?.root} type="simple">
                  <Tooltip.Trigger
                    {...tooltipProps?.trigger}
                    className={informationIconClasses}
                    hasButtonWrapper
                  >
                    <div
                      tabIndex={0}
                      role="button"
                      aria-label="Information icon"
                    >
                      <InformationCircleIconOutline />
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Content {...tooltipProps?.content}>
                    {moreInformationText}
                  </Tooltip.Content>
                </Tooltip>
              )}
              {showRequiredOrOptionalLabel && (
                <Typography
                  variant={size === 'large' ? 'body-large' : 'body-medium'}
                  className="ndl-form-item-optional"
                >
                  {isRequired === true ? 'Required' : 'Optional'}
                </Typography>
              )}
            </div>
          </Skeleton>
        )}
        <Skeleton
          onBackground="weak"
          shape="rectangular"
          {...skeletonProps}
          isLoading={isSkeletonLoading}
        >
          <div className="ndl-input-wrapper">
            {leadingElement && (
              <div className="ndl-element-leading ndl-element">
                {leadingElement}
              </div>
            )}
            <input
              readOnly={isReadOnly}
              disabled={isDisabled}
              required={isRequired}
              value={value}
              placeholder={placeholder}
              type="text"
              onChange={onChange}
              {...combinedHtmlInputAttributes}
              {...restProps}
              ref={ref}
            />
            {trailingElement && (
              <div className="ndl-element-trailing ndl-element">
                {trailingElement}
              </div>
            )}
          </div>
        </Skeleton>
      </label>
      {Boolean(helpText) && !errorText && (
        <Skeleton
          onBackground="weak"
          shape="rectangular"
          isLoading={isSkeletonLoading}
        >
          <Typography
            variant={size === 'large' ? 'body-medium' : 'body-small'}
            className="ndl-form-message"
          >
            {helpText}
          </Typography>
        </Skeleton>
      )}
      {Boolean(errorText) && (
        // TODO v4: We might want to have a min width for the container for the messages to help skeleton loading.
        // Currently the message fills 100% of the width while the rest of the text input has a set width.
        <Skeleton
          onBackground="weak"
          shape="rectangular"
          width="fit-content"
          {...skeletonProps}
          isLoading={isSkeletonLoading}
        >
          <div className="ndl-form-message">
            <div className="ndl-error-icon">
              <ExclamationCircleIconSolid />
            </div>
            <Typography
              className="ndl-error-text"
              variant={size === 'large' ? 'body-medium' : 'body-small'}
            >
              {errorText}
            </Typography>
          </div>
        </Skeleton>
      )}
    </div>
  );
};
